<!--
* @Author: AI Assistant
* @Date: 2026-01-13
* @Description: API Contact 页面 - 联系我们
-->
<template>
    <div class="contact-page">
        <h1 class="sr-only">{{ t('api.contact') }}</h1>
        <div class="contact-shell">
            <header class="contact-hero">
                <div class="contact-icon" aria-hidden="true">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="25"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                    </svg>
                </div>
                <p class="contact-eyebrow">{{ t('api.businessInquiry') }}</p>
                <h2>{{ t('api.businessContactTitle') }}</h2>
                <p class="contact-desc">{{ t('api.businessContactDesc') }}</p>
            </header>

            <section class="business-benefits">
                <article class="business-benefit">
                    <div class="benefit-icon" aria-hidden="true">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M12 2v20" />
                            <path
                                d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                            />
                        </svg>
                    </div>
                    <h3>{{ t('api.lowerTokenPriceTitle') }}</h3>
                    <p>{{ t('api.lowerTokenPriceDesc') }}</p>
                </article>
                <article class="business-benefit">
                    <div class="benefit-icon" aria-hidden="true">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <rect x="4" y="4" width="16" height="16" rx="2" />
                            <path d="M9 9h6v6H9z" />
                            <path
                                d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3"
                            />
                        </svg>
                    </div>
                    <h3>{{ t('api.higherQuotaTitle') }}</h3>
                    <p>{{ t('api.higherQuotaDesc') }}</p>
                </article>
                <article class="business-benefit">
                    <div class="benefit-icon" aria-hidden="true">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path
                                d="m12 3-1.9 4.8a2 2 0 0 1-1.1 1.1L4 11l5 2.1a2 2 0 0 1 1.1 1.1L12 19l1.9-4.8a2 2 0 0 1 1.1-1.1L20 11l-5-2.1a2 2 0 0 1-1.1-1.1L12 3Z"
                            />
                        </svg>
                    </div>
                    <h3>{{ t('api.earlyAccessTitle') }}</h3>
                    <p>{{ t('api.earlyAccessDesc') }}</p>
                </article>
            </section>

            <section
                class="contact-panel"
                aria-labelledby="contact-email-title"
            >
                <div class="contact-details">
                    <p id="contact-email-title" class="contact-email-label">
                        {{ t('api.businessEmail') }}
                    </p>
                    <a href="mailto:business@wery.ai" class="contact-email">
                        business@wery.ai
                    </a>
                    <p class="contact-note">{{ t('api.businessEmailHint') }}</p>
                    <p class="support-time">{{ t('api.supportTime') }}</p>
                </div>
                <a
                    href="mailto:business@wery.ai?subject=WeryAI%20API%20Business%20Inquiry"
                    class="email-cta"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                    </svg>
                    {{ t('api.contactSales') }}
                </a>
            </section>

            <div class="docs-link-row">
                <span>{{ t('api.integrationHelp') }}</span>
                <NuxtLink
                    to="https://docs.weryai.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {{ t('api.viewDocs') }}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M7 17 17 7" />
                        <path d="M7 7h10v10" />
                    </svg>
                </NuxtLink>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { definePageMeta, useHead, useRequestURL } from '#imports'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useI18n } from '~/composables/useI18n'
import { computed } from 'vue'

const { t } = useI18n()

// 使用多语言 SEO
useMultilingualSeo({ pageType: 'apiContact', robots: 'noindex, follow' })

// 结构化数据
const requestUrlHref = useRequestURL().href

useHead({
    script: [
        // BreadcrumbList Schema
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'WeryAI',
                            item: 'https://www.weryai.com',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('api.contact'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})
</script>

<style scoped lang="less">
.contact-page {
    display: flex;
    min-height: calc(100vh - 128px);
    align-items: center;
    justify-content: center;
    padding: 48px 28px 64px;
    animation: fadeIn 0.3s ease-out;

    @media (max-width: 768px) {
        align-items: flex-start;
        padding: 30px 14px 44px;
    }
}

.contact-shell {
    width: 100%;
    max-width: 820px;
}

.contact-hero {
    max-width: 650px;
    margin: 0 auto 34px;
    text-align: center;

    h2 {
        margin: 0;
        color: #fff;
        font-size: 30px;
        font-weight: 700;
        line-height: 1.35;

        @media (max-width: 768px) {
            font-size: 23px;
        }
    }
}

.contact-icon {
    display: inline-flex;
    width: 58px;
    height: 58px;
    align-items: center;
    justify-content: center;
    margin-bottom: 17px;
    border: 1px solid rgba(153, 247, 104, 0.24);
    border-radius: 50%;
    color: #99f768;
    background: #181a18;
    box-shadow: 0 12px 34px rgba(153, 247, 104, 0.08);
}

.contact-eyebrow {
    margin: 0 0 8px;
    color: #99f768;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.4;
    text-transform: uppercase;
}

.contact-desc {
    margin: 13px 0 0;
    color: #a1a1aa;
    font-size: 15px;
    line-height: 1.75;

    @media (max-width: 768px) {
        font-size: 13px;
        line-height: 1.65;
    }
}

.business-benefits {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-bottom: 22px;
    border-top: 1px solid #27272a;
    border-bottom: 1px solid #27272a;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.business-benefit {
    min-width: 0;
    padding: 24px 22px;

    & + & {
        border-left: 1px solid #27272a;
    }

    h3 {
        margin: 14px 0 6px;
        color: #f4f4f5;
        font-size: 14px;
        font-weight: 650;
        line-height: 1.5;
    }

    p {
        margin: 0;
        color: #8f8f98;
        font-size: 12px;
        line-height: 1.65;
    }

    @media (max-width: 768px) {
        display: grid;
        grid-template-columns: 36px 1fr;
        column-gap: 13px;
        padding: 18px 4px;

        & + & {
            border-top: 1px solid #27272a;
            border-left: 0;
        }

        h3 {
            margin: 0 0 4px;
        }

        p {
            grid-column: 2;
        }
    }
}

.benefit-icon {
    display: flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(153, 247, 104, 0.2);
    border-radius: 8px;
    color: #99f768;
    background: rgba(153, 247, 104, 0.055);
}

.contact-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 28px;
    padding: 24px 26px;
    border: 1px solid #2f3330;
    border-radius: 10px;
    background: #181a18;

    @media (max-width: 768px) {
        align-items: stretch;
        flex-direction: column;
        gap: 19px;
        padding: 20px 18px;
    }
}

.contact-details {
    min-width: 0;
}

.contact-email-label {
    margin: 0 0 3px;
    color: #99f768;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.4;
    text-transform: uppercase;
}

.contact-email {
    color: #fff;
    font-size: 19px;
    font-weight: 650;
    line-height: 1.5;
    overflow-wrap: anywhere;
    text-decoration: none;

    &:hover {
        color: #99f768;
    }
}

.contact-note,
.support-time {
    margin: 6px 0 0;
    color: #a1a1aa;
    font-size: 12px;
    line-height: 1.55;
}

.support-time {
    margin-top: 3px;
    color: #71717a;
}

.email-cta {
    display: inline-flex;
    flex: 0 0 auto;
    min-height: 44px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 20px;
    border-radius: 8px;
    color: #080b08;
    background: #99f768;
    font-size: 13px;
    font-weight: 650;
    line-height: 1.4;
    text-decoration: none;
    transition:
        background 0.2s ease,
        transform 0.2s ease;

    &:hover {
        background: #adff82;
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 3px;
    }
}

.docs-link-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 19px;
    color: #71717a;
    font-size: 12px;
    line-height: 1.5;

    a {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #d4d4d8;
        font-weight: 600;
        text-decoration: none;

        &:hover {
            color: #99f768;
        }
    }
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
